/* ==========================================================================================
   OPTICS TRADER — HOMEPAGE BEHAVIOUR
   Modern vanilla JS only. Loaded as a module (deduped by the browser even when several
   sections include the same script tag). Safe with Shopify Theme Editor section reloads:
   - <ot-hero-slider> and <ot-marquee> are custom elements, so they self-initialise
     whenever the editor injects fresh section markup.
   - Scroll-reveal is (re)applied on shopify:section:load without duplicate observers.
   ========================================================================================== */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------------------------------------------------------------- scroll reveal */
const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        entry.target.classList.add('ot-reveal--in');
        revealObserver.unobserve(entry.target);
      }
    }
  },
  { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
);

function initReveals(root = document) {
  if (REDUCED_MOTION.matches) return;
  for (const el of root.querySelectorAll('.ot-reveal:not(.ot-reveal--ready)')) {
    // Elements already in the viewport stay visible; only below-the-fold items animate in.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) continue;
    el.classList.add('ot-reveal--ready');
    revealObserver.observe(el);
  }
}

/* ---------------------------------------------------------------- hero slider */
class OtHeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-ot-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-ot-dot]'));
    this.counter = this.querySelector('[data-ot-counter-current]');
    this.index = 0;
    this.timer = null;
    this.interval = parseInt(this.dataset.interval, 10) || 7000;
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.style.setProperty('--ot-hero-interval', `${this.interval}ms`);

    if (this.slides.length <= 1) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.querySelector('[data-ot-prev]')?.addEventListener('click', () => this.goAndReset(this.index - 1), { signal });
    this.querySelector('[data-ot-next]')?.addEventListener('click', () => this.goAndReset(this.index + 1), { signal });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goAndReset(i), { signal }));

    this.addEventListener('keydown', (event) => {
      if (event.target.closest('[data-ot-dot], [data-ot-prev], [data-ot-next]')) {
        if (event.key === 'ArrowRight') this.goAndReset(this.index + 1);
        if (event.key === 'ArrowLeft') this.goAndReset(this.index - 1);
      }
    }, { signal });

    // Pause while the user is reading or interacting
    this.addEventListener('mouseenter', () => this.stop(), { signal });
    this.addEventListener('mouseleave', () => this.play(), { signal });
    this.addEventListener('focusin', () => this.stop(), { signal });
    this.addEventListener('focusout', () => this.play(), { signal });
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.play()), { signal });
    REDUCED_MOTION.addEventListener?.('change', () => (REDUCED_MOTION.matches ? this.stop() : this.play()), { signal });

    // Theme editor: show the slide whose block is selected, pause while selected
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest?.('[data-ot-slide]');
        if (slide && this.contains(slide)) {
          this.go(this.slides.indexOf(slide));
          this.stop();
        }
      }, { signal });
      document.addEventListener('shopify:block:deselect', (event) => {
        if (this.contains(event.target)) this.play();
      }, { signal });
    }

    this.play();
  }

  disconnectedCallback() {
    this.stop();
    this.abortController?.abort();
  }

  go(n) {
    const previous = this.index;
    this.index = (n + this.slides.length) % this.slides.length;
    if (this.index === previous) return;

    this.slides[previous].classList.remove('ot-active');
    this.slides[previous].setAttribute('aria-hidden', 'true');
    this.slides[this.index].classList.add('ot-active');
    this.slides[this.index].removeAttribute('aria-hidden');

    this.dots[previous]?.classList.remove('ot-active');
    this.dots[previous]?.setAttribute('aria-selected', 'false');
    const activeDot = this.dots[this.index];
    if (activeDot) {
      activeDot.classList.add('ot-active');
      activeDot.setAttribute('aria-selected', 'true');
      // Restart the progress-fill animation
      activeDot.style.animation = 'none';
      void activeDot.offsetWidth;
      activeDot.style.animation = '';
    }

    if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');

    // Lazy videos: play only the active slide's video
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === this.index) video.play?.().catch(() => {});
      else video.pause?.();
    });
  }

  goAndReset(n) {
    this.go(n);
    this.play();
  }

  play() {
    if (!this.autoplayEnabled || REDUCED_MOTION.matches) return;
    this.stop();
    this.classList.add('ot-hero--playing');
    this.timer = setInterval(() => this.go(this.index + 1), this.interval);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.classList.remove('ot-hero--playing');
  }
}

if (!customElements.get('ot-hero-slider')) {
  customElements.define('ot-hero-slider', OtHeroSlider);
}

/* ---------------------------------------------------------------- brand marquee */
class OtMarquee extends HTMLElement {
  connectedCallback() {
    if (REDUCED_MOTION.matches) return;
    const track = this.querySelector('[data-ot-marquee-track]');
    if (!track || track.dataset.duplicated) return;
    // Duplicate the tile set once so the CSS keyframe loop is seamless
    const clone = track.cloneNode(true);
    for (const child of Array.from(clone.children)) {
      child.setAttribute('aria-hidden', 'true');
      child.setAttribute('tabindex', '-1');
      track.appendChild(child);
    }
    track.dataset.duplicated = 'true';
  }
}

if (!customElements.get('ot-marquee')) {
  customElements.define('ot-marquee', OtMarquee);
}

/* ---------------------------------------------------------------- boot + editor events */
function boot(root = document) {
  initReveals(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot());
} else {
  boot();
}

document.addEventListener('shopify:section:load', (event) => boot(event.target));
